import { useCallback, useState, useEffect } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Button } from "react-bootstrap";
import { useHistory, Link } from "react-router-dom";
import Auth from "../services/auth";
import TextBox from "../components/controls/form/TextBox";
import { useTranslation } from "react-i18next";
import PubSub from "../services/PubSub";
import { useQuery } from "../common/Hooks";
import Utils from "../utils/utils";
import LandingPage from "./components/LandingPage";
import Amplitude from "../services/amplitude";

const Login = () => {
    const query = useQuery();
    const defaultEmail = query.get("email") || window.localStorage.getItem("email") || "";
    const [translate] = useTranslation();
    const [rememberMe, setRememberMe] = useState(window.localStorage.getItem("rememberMe") === "true");
    const history = useHistory();
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [canTryGuestLogin, setCanTryGuestLogin] = useState(true);

    const login = useCallback(async ({ email, password }) => {
        setButtonDisabled(true);
        if (rememberMe) {
            window.localStorage.setItem("email", email);
        } else {
            window.localStorage.removeItem("email");
        }
        window.localStorage.setItem("rememberMe", rememberMe);
        const response = await Auth.login({ email, password });

        setButtonDisabled(false);
        if (!response.error) {
            history.push("/");
        } else {
            PubSub.publish("show_info", response.data.error);
        }
    }, [history, rememberMe]);

    const loginGuest = useCallback(async (guestMode) => {
        if (!canTryGuestLogin) {
            return;
        }
        setButtonDisabled(true);
        setCanTryGuestLogin(false);

        const response = await Auth.loginGuest({ email: guestMode });
        if (response && !response.error) {
            if (response.data && response.data.template) {
                window.sessionStorage.setItem("guestTemplate", JSON.stringify(response.data.template));
                Amplitude.logAmplitute({
                    eventType: "Launched TryIt",
                    guestSession: response.data.template.guestSessionId
                });
                history.push(Utils.routeToGuestHomePage());
            }
        } else {
            PubSub.publish("show_info", response.data.error);
        }

        setButtonDisabled(false);
    }, [canTryGuestLogin, history]);

    useEffect(() => {
        const guestMode = query.get("guestMode");

        if (guestMode) {
            loginGuest(guestMode);
        }
    }, [query, loginGuest]);

    return (
        <LandingPage>
            {query.get("guestMode") ? null : <Formik
                initialValues={{
                    email: defaultEmail,
                    password: ""
                }}
                validationSchema={Yup.object({
                    email: Yup.string()
                        .email(translate("OTM_INVALID_EMAIL"))
                        .required(" "),
                    password: Yup.string()
                        .min(4, translate("OTM_CHARACTERS_OR_MORE", { minCharacters: 4 }))
                        .required(" ")
                })}
                onSubmit={login}
            >
                <Form>
                    <TextBox
                        id="login-email"
                        placeholder={translate("OTM_EMAIL")}
                        type="email"
                        name="email"
                        css=""
                    />
                    <TextBox placeholder={translate("OTM_PASSWORD")} type="password" name="password" css="mt-3" />
                    <div className="mt-3">
                        <Link to={`/reset-password?email=${document.getElementById('login-email')?.value || ''}`}>
                            {translate("OTM_PASSWORD_FORGOTTEN")}
                        </Link>
                    </div>
                    <div className="mt-3 form-check">
                        <input type="checkbox" className="form-check-input" id="rememberMe"
                            checked={rememberMe} onChange={() => setRememberMe(!rememberMe)}></input>
                        <label htmlFor="rememberMe" className="form-check-label">{translate("OTM_REMEMBER_ME")}</label>
                    </div>
                    <div className="text-center mt-3">
                        <Button type="submit" className="btn-primary" disabled={buttonDisabled}>{translate("OTM_LOGIN")}</Button>
                    </div>
                </Form>
            </Formik>}
        </LandingPage>
    );
};

export default Login;
